
import { motion } from 'framer-motion';
import { Calendar } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { typography } from '../../../styles/typography';
import PrimaryButton from '../../ui/PrimaryButton';
import { closingStockApi } from './CloseingStockApi';

const getTodayDate = () => new Date().toISOString().split('T')[0];

// Dummy sites data
const dummySites = [
  { site_id: 'site_001', site_name: 'Bangalore Main Office' },
  { site_id: 'site_002', site_name: 'Mumbai Branch' },
  { site_id: 'site_003', site_name: 'Delhi Office' },
];

// Helper to get dates in current month for opening stock
const getCurrentMonthDates = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day1 = new Date(year, month, 12).toISOString().split('T')[0];
  const day2 = new Date(year, month, 15).toISOString().split('T')[0];
  const day3 = new Date(year, month, 18).toISOString().split('T')[0];
  const day4 = new Date(year, month, 19).toISOString().split('T')[0];
  const day5 = new Date(year, month, 20).toISOString().split('T')[0];
  return { day1, day2, day3, day4, day5 };
};

// Generate dummy closing stock data with construction materials
const getDummyClosingStockData = () => {
  const { day1, day2, day3, day4, day5 } = getCurrentMonthDates();
  
  return [
    {
      id: 1,
      name: 'Cement',
      quantity: 500,
      closingQuantity: 350,
      unit: 'Bags',
      openingStockDate: day1,
      siteId: 'site_001',
      siteName: 'Bangalore Main Office',
    },
    {
      id: 2,
      name: 'Bricks',
      quantity: 10000,
      closingQuantity: 7500,
      unit: 'Pieces',
      openingStockDate: day2,
      siteId: 'site_001',
      siteName: 'Bangalore Main Office',
    },
    {
      id: 3,
      name: 'Aggregates',
      quantity: 50,
      closingQuantity: 35,
      unit: 'Cubic Meters',
      openingStockDate: day3,
      siteId: 'site_002',
      siteName: 'Mumbai Branch',
    },
    {
      id: 4,
      name: 'Steel & Reinforcement',
      quantity: 25,
      closingQuantity: 18,
      unit: 'Tons',
      openingStockDate: day4,
      siteId: 'site_002',
      siteName: 'Mumbai Branch',
    },
    {
      id: 5,
      name: 'Sand',
      quantity: 100,
      closingQuantity: 70,
      unit: 'Cubic Meters',
      openingStockDate: day5,
      siteId: 'site_003',
      siteName: 'Delhi Office',
    },
  ];
};

const closingStockItems = getDummyClosingStockData();

const Closing_Stock = () => {
  const location = useLocation();
  const passedData = location.state || {};
  
  // Use passed data if available, otherwise use dummy data
  const itemsFromView = passedData.items || [];
  const selectedDate = passedData.date || getTodayDate();
  const selectedSiteId = passedData.siteId;
  const selectedSiteName = passedData.siteName;
  
  // Fetch closing stock data if not provided from view
  useEffect(() => {
    const fetchClosingStock = async () => {
      // If items are passed from view, use them
      if (itemsFromView.length > 0) {
        setActualClosingStockItems(itemsFromView);
        return;
      }

      // Otherwise, fetch from API
      setIsLoadingData(true);
      try {
        const result = await closingStockApi.getClosingStock({
          limit: 500,
          date: selectedDate,
          opening_date: selectedDate,
        });

        if (result.success && result.items) {
          // Store summary data
          setSummary(result.summary);
          
          // Transform API response - current_quantity is closing stock per API docs
          const transformedItems = result.items.map((item, index) => ({
            id: item.stock_item_id || `item-${index}`,
            name: item.item_name || 'Unknown Item',
            quantity: item.opening_quantity || item.current_quantity || 0,
            closingQuantity: item.current_quantity || 0, // current_quantity is closing stock
            unit: item.unit_of_measure || 'units',
            stock_item_id: item.stock_item_id,
            vendor_name: item.vendor_name,
            reorder_threshold: item.reorder_threshold,
            site_id: item.site_id,
            site_name: item.site_name,
            price_per_unit: item.price_per_unit,
            is_active: item.is_active,
            opening_value: item.opening_value,
            is_low_stock: item.reorder_threshold > 0 && item.current_quantity <= item.reorder_threshold,
            openingStockDate: selectedDate,
            siteId: selectedSiteId || item.site_id,
            siteName: selectedSiteName || item.site_name,
          }));
          setActualClosingStockItems(transformedItems);
        } else {
          console.warn('Failed to fetch closing stock:', result.message);
          setActualClosingStockItems([]);
          setSummary(null);
        }
      } catch (err) {
        console.error('Error fetching closing stock:', err);
        setActualClosingStockItems([]);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchClosingStock();
  }, [itemsFromView.length, selectedDate, selectedSiteId, selectedSiteName]);

  // Use passed items if available, otherwise use fetched data or fall back to dummy data
  const closingStockItemsToUse = itemsFromView.length > 0 
    ? itemsFromView 
    : actualClosingStockItems.length > 0
    ? actualClosingStockItems
    : closingStockItems;
  
  const [sites] = useState(dummySites);

  const [date, setDate] = useState(() => selectedDate);
  const [errors, setErrors] = useState({
    date: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [isMobileView, setIsMobileView] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [actualClosingStockItems, setActualClosingStockItems] = useState([]);
  const [summary, setSummary] = useState(passedData.summary || null);


  const handleDownload = (row) => {
    // Generate CSV data for download
    const itemName = row.name || 'N/A';
    const quantity = row.quantity ? `${row.quantity} ${row.unit}` : 'N/A';
    const siteName = row.siteName || row.site_name || sites.find(s => s.site_id === row.siteId || s.site_id === row.site_id)?.site_name || 'N/A';
    
    const csvData = [
      ['Item Name', 'Item ID', 'Quantity', 'Site'],
      [
        itemName,
        row.id,
        quantity,
        siteName,
      ],
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `closing-stock-${itemName}-${row.id}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)');

    const updateView = (event) => {
      setIsMobileView(event.matches);
    };

    updateView(mediaQuery);
    mediaQuery.addEventListener('change', updateView);

    return () => mediaQuery.removeEventListener('change', updateView);
  }, []);

  const handleDateChange = (value) => {
    setDate(value);
    setErrors((prev) => ({ ...prev, date: '' }));
    setSuccess('');
    setError('');
  };

  const validateForm = () => {
    const dateError = date ? '' : 'Date is required';

    setErrors({
      date: dateError,
    });

    return !dateError;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      setError('Please fill in the date.');
      return;
    }

    setIsSaving(true);
    setSuccess('');
    setError('');

    try {
      // TODO: Replace with actual API call
      console.log('Closing stock payload', {
        date,
        items: closingStockItemsToUse,
      });

      await new Promise((resolve) => setTimeout(resolve, 1000));
      setSuccess('Closing stock saved successfully!');
    } catch (err) {
      console.error('Failed to save closing stock', err);
      setError('Unable to save closing stock. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto h-auto">
        <form onSubmit={handleSubmit} className="h-full space-y-8">
            <motion.div
              variants={cardVariants}
              initial="hidden"
              animate="visible"
            >
              <div className="rounded-3xl">
                {/* Summary Card */}
                {summary && (
                  <div className="mb-6 p-6 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl">
                    <h3 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
                      Closing Stock Summary
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <span className="text-gray-600 text-sm block mb-1">Total Items</span>
                        <span className="font-bold text-xl">
                          {summary.total_items?.toLocaleString('en-IN') || '0'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 text-sm block mb-1">Total Quantity</span>
                        <span className="font-bold text-xl">
                          {summary.total_quantity?.toLocaleString('en-IN', { maximumFractionDigits: 2 }) || '0.00'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 text-sm block mb-1">Low Stock Items</span>
                        <span className="font-bold text-xl text-orange-600">
                          {summary.low_stock_items?.toLocaleString('en-IN') || '0'}
                        </span>
                      </div>
                      {summary.opening_quantity !== undefined && (
                        <div>
                          <span className="text-gray-600 text-sm block mb-1">Opening Quantity</span>
                          <span className="font-bold text-xl">
                            {summary.opening_quantity?.toLocaleString('en-IN', { maximumFractionDigits: 2 }) || '0.00'}
                          </span>
                        </div>
                      )}
                      {summary.opening_value !== undefined && (
                        <div>
                          <span className="text-gray-600 text-sm block mb-1">Opening Value</span>
                          <span className="font-bold text-xl text-green-600">
                            ₹{summary.opening_value?.toLocaleString('en-IN', { maximumFractionDigits: 2 }) || '0.00'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-4">
                  <div className="flex-1">
                    <label
                      htmlFor="closing-stock-date"
                      className="inline-flex items-center gap-2"
                    >
                      Pick Opening Stock Date
                    </label>
                    <div className="relative mt-1 max-w-xs">
                      <Calendar className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                      <input
                        type="date"
                        id="closing-stock-date"
                        value={date}
                        onChange={(e) => handleDateChange(e.target.value)}
                        className={`w-full h-12 rounded-2xl border bg-white pl-10 pr-4 text-sm focus:outline-none focus:ring-2 ${
                          errors.date
                            ? 'border-red-500 focus:ring-red-500'
                            : 'border-gray-200 focus:ring-green-500'
                        }`}
                        required
                      />
                    </div>
                    {errors.date && (
                      <p className="mt-1 text-xs text-red-600">{errors.date}</p>
                    )}
                  </div>
                {/* <div className="md:text-right">
                  <PrimaryButton
                    type="submit"
                    disabled={isSaving}
                    className="w-full md:w-auto min-w-[200px]"
                  >
                    {isSaving ? (
                      <span className="flex items-center gap-2">
                        <svg
                          className="h-5 w-5 animate-spin text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Saving...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Save className="w-5 h-5" />
                        Save
                      </span>
                    )}
                  </PrimaryButton>
                </div> */}
              </div>

              {!isMobileView && (
                <div className="overflow-x-auto rounded-3xl border border-gray-200 bg-white shadow-sm">
                  <table className="min-w-full border-collapse text-sm md:text-base">
                    <thead>
                      <tr className="bg-gray-50 text-left text-gray-600">
                        <th className="px-4 py-3 font-semibold">S.No</th>
                        <th className="px-4 py-3 font-semibold">Items</th>
                        <th className="px-4 py-3 font-semibold">
                          Opening Stock
                        </th>
                        <th className="px-4 py-3 font-semibold">Closing Stock</th>
                        <th className="px-4 py-3 font-semibold">Consumption</th>
                      </tr>
                    </thead>
                    <tbody>
                      {closingStockItemsToUse.map((item, index) => {
                        const isLowStock = item.reorder_threshold > 0 && item.closingQuantity <= item.reorder_threshold;
                        return (
                        <tr
                          key={item.id}
                          className={`border-t border-gray-100 align-top text-gray-800 ${
                            isLowStock ? 'bg-orange-50' : ''
                          }`}
                        >
                          <td className="px-4 py-4">{index + 1}</td>
                          <td className="px-4 py-4">
                            <div className="font-semibold">{item.name}</div>
                            <p className="text-xs text-gray-500">
                              Item ID: {item.id}
                            </p>
                            {isLowStock && (
                              <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold text-orange-700 bg-orange-100 rounded">
                                Low Stock
                              </span>
                            )}
                            {item.vendor_name && (
                              <p className="text-xs text-gray-500 mt-1">
                                Vendor: {item.vendor_name}
                              </p>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="h-11 rounded-2xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-600 flex items-center">
                              {item.quantity} {item.unit}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className={`h-11 rounded-2xl border px-4 text-sm flex items-center ${
                              isLowStock 
                                ? 'border-orange-300 bg-orange-50 text-orange-700 font-semibold' 
                                : 'border-gray-200 bg-gray-100 text-gray-600'
                            }`}>
                              {item.closingQuantity} {item.unit}
                              {item.reorder_threshold > 0 && (
                                <span className="ml-2 text-xs text-gray-500">
                                  (Threshold: {item.reorder_threshold})
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="h-11 rounded-2xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-600 flex items-center">
                              {item.quantity - item.closingQuantity} {item.unit}
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {isMobileView && (
                <div className="space-y-4">
                  {closingStockItemsToUse.map((item, index) => {
                    const isLowStock = item.reorder_threshold > 0 && item.closingQuantity <= item.reorder_threshold;
                    return (
                    <div
                      key={item.id}
                      className={`rounded-2xl border p-4 shadow-sm ${
                        isLowStock 
                          ? 'border-orange-300 bg-orange-50' 
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="mb-3">
                        <div className="text-sm font-medium text-gray-500">Item #{index + 1}</div>
                      </div>
                      <div className="mb-3">
                        <div className="font-semibold text-gray-800">{item.name}</div>
                        <p className="text-xs text-gray-500">Item ID: {item.id}</p>
                        {isLowStock && (
                          <span className="inline-block mt-1 px-2 py-0.5 text-xs font-semibold text-orange-700 bg-orange-100 rounded">
                            Low Stock
                          </span>
                        )}
                        {item.vendor_name && (
                          <p className="text-xs text-gray-500 mt-1">
                            Vendor: {item.vendor_name}
                          </p>
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-3">
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Opening Stock</p>
                          <div className="h-11 rounded-2xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-600 flex items-center">
                            {item.quantity} {item.unit}
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Closing Stock</p>
                          <div className={`h-11 rounded-2xl border px-4 text-sm flex items-center ${
                            isLowStock 
                              ? 'border-orange-300 bg-orange-50 text-orange-700 font-semibold' 
                              : 'border-gray-200 bg-gray-100 text-gray-600'
                          }`}>
                            {item.closingQuantity} {item.unit}
                            {item.reorder_threshold > 0 && (
                              <span className="ml-2 text-xs text-gray-500">
                                (Threshold: {item.reorder_threshold})
                              </span>
                            )}
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Consumption</p>
                          <div className="h-11 rounded-2xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-600 flex items-center">
                            {item.quantity - item.closingQuantity} {item.unit}
                          </div>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}

              {/* Pick Closing Stock Date Section */}
              <div className="mt-8 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                {/* <h5
                  className={typography.h5}
                  style={{ color: '#000000', marginBottom: '0.5rem' }}
                >
                  Pick Closing Stock Date:
                </h5> */}
                <p className={typography.p} style={{ color: '#000000', marginBottom: '1rem' }}>
                  Select the date for closing stock
                </p>
                <div className="relative max-w-xs">
                  <Calendar className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => handleDateChange(e.target.value)}
                    className={`w-full h-12 rounded-2xl border bg-white pl-10 pr-4 text-sm focus:outline-none focus:ring-2 ${
                      errors.date
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-gray-200 focus:ring-green-500'
                    }`}
                    required
                  />
                </div>
                {errors.date && (
                  <p className="mt-1 text-xs text-red-600">{errors.date}</p>
                )}
              </div>

              {/* Close Stock Button */}
              <div className="mt-6 flex justify-end">
                <PrimaryButton
                  type="submit"
                  disabled={isSaving}
                  className="w-full md:w-auto min-w-[200px]"
                >
                  {isSaving ? (
                    <span className="flex items-center gap-2">
                      <svg
                        className="h-5 w-5 animate-spin text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    'Close Stock'
                  )}
                </PrimaryButton>
              </div>
            </div>
          </motion.div>

            {(success || error) && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`rounded-lg p-4 ${
                  success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}
              >
                {success || error}
              </motion.div>
            )}
        </form>
      </div>
    </div>
  );
};

export default Closing_Stock;
